import type { FileUploadStatus } from '@/shared/hooks/api/productSource'
import {
  isProductSourceSpreadsheetContentType,
  isProductSourceSpreadsheetFileName,
} from '@/features/product-catalog/utils/productSourceFileTypes'

/** English defaults passed as i18n fallback (second arg to `t`). */
export const INGESTION_ERROR_CODE_DEFAULTS: Record<string, string> = {
  INGEST_DOWNLOAD_FAILED:
    'This document could not be downloaded from the URL. Check the link or try again.',
  INGEST_FILE_TOO_LARGE: 'This file is larger than the maximum allowed size.',
  INGEST_UNSUPPORTED_MEDIA_TYPE:
    'This file type is not supported. Upload a PDF, JPEG, PNG, XLSX, XLSM, XLS, or CSV.',
  INGEST_IMAGE_INVALID_OR_CORRUPT:
    'This file could not be processed. It may be unreadable or use an unsupported image encoding.',
  INGEST_LOCAL_FILE_ERROR:
    'The file path is invalid or the file could not be found.',
  INGEST_PDF_INVALID_OR_CORRUPT:
    'This PDF could not be read. It may be damaged or not a valid PDF.',
  INGEST_PDF_BLANK: 'This PDF is blank, no text could be extracted.',
  INGEST_PDF_NO_TEXT:
    'This PDF contains images, but no text could be extracted.',
  INGEST_TEXT_INPUT_EMPTY: 'No text was entered.',
  INGEST_DATABASE_ERROR:
    'We could not save the ingested content. Please try again later.',
  INGEST_UNKNOWN:
    'We could not process this source. Try again or use a different file.',
  INGEST_RETRIES_EXCEEDED:
    'Document processing timed out after multiple attempts. Try again in a moment.',
  INGEST_OCR_VLM_UNAVAILABLE:
    'Document vision extraction is temporarily unavailable. Please retry in a few minutes.',
  INGEST_PUBLISH_FAILED:
    'We could not publish the processed document. Please retry in a few minutes.',
  INGEST_EXTRACTION_EXHAUSTED:
    'This file could not be processed. Document extraction did not complete.',
  INGEST_SPREADSHEET_EMPTY:
    'This spreadsheet is empty. No data could be extracted.',
  INGEST_SPREADSHEET_INVALID:
    'This spreadsheet could not be read. It may be damaged or not a valid file.',
  INGEST_SPREADSHEET_UNREADABLE:
    'This spreadsheet could not be read. It may be password-protected or damaged.',
  INGEST_DOCUMENT_RIGHTS_PROTECTED:
    'This file is rights-protected and cannot be processed. Save a copy without the sensitivity label or password and upload again.',
  INGEST_CAD_PARSE_FAILED:
    'This CAD drawing could not be read. It may be damaged or an unsupported DWG version — re-export and upload again.',
}

export const INGESTION_ERROR_GENERIC_DEFAULT =
  'We could not process this source. Try again or use a different file.'

export type IngestionFailureTranslate = (
  key: string,
  defaultValue: string
) => string

export type IngestionFailureSourceContext = {
  sourceContentType?: string | null
  sourceFileName?: string | null
}

function isSpreadsheetProductSource(
  context: IngestionFailureSourceContext | undefined
): boolean {
  if (context == null) {
    return false
  }
  if (isProductSourceSpreadsheetContentType(context.sourceContentType)) {
    return true
  }
  return isProductSourceSpreadsheetFileName(context.sourceFileName)
}

/** Legacy PDF/rights codes remapped for spreadsheet product sources (message + actions). */
export function getEffectiveIngestionErrorCode(
  code: string | null | undefined,
  context?: IngestionFailureSourceContext
): string {
  const trimmedCode = typeof code === 'string' ? code.trim() : ''
  if (trimmedCode.length === 0) {
    return trimmedCode
  }
  if (!isSpreadsheetProductSource(context)) {
    return trimmedCode
  }
  if (trimmedCode === 'INGEST_DOCUMENT_RIGHTS_PROTECTED') {
    return 'INGEST_SPREADSHEET_UNREADABLE'
  }
  if (trimmedCode === 'INGEST_PDF_INVALID_OR_CORRUPT') {
    return 'INGEST_SPREADSHEET_INVALID'
  }
  return trimmedCode
}

/**
 * User-facing ingestion failure line. Prefer locale keys under
 * `sources.status.ingestionErrorCodes.*`; English defaults are the fallback
 * when a translation is missing.
 */
export function getIngestionFailureMessage(
  code: string | null | undefined,
  translate: IngestionFailureTranslate,
  context?: IngestionFailureSourceContext
): string {
  const trimmedCode = typeof code === 'string' ? code.trim() : ''
  if (trimmedCode) {
    const effectiveCode = getEffectiveIngestionErrorCode(trimmedCode, context)
    const key = `status.ingestionErrorCodes.${effectiveCode}`
    const defaultText =
      INGESTION_ERROR_CODE_DEFAULTS[effectiveCode] ??
      INGESTION_ERROR_GENERIC_DEFAULT
    return translate(key, defaultText)
  }
  return translate(
    'status.ingestionErrorGeneric',
    INGESTION_ERROR_GENERIC_DEFAULT
  )
}

const UPLOAD_FAILED_DEFAULT = 'Failed to upload PDF. Please try again.'

export type ResolveIngestionFailureTextInput = {
  ingestionError?: string | null
  ingestionErrorCode?: string | null
  fileUploadStatus?: FileUploadStatus | null
  sourceContentType?: string | null
  sourceFileName?: string | null
  translate: IngestionFailureTranslate
}

/**
 * Full user-facing ingestion failure text. Prefers mapped error-code copy whenever
 * `ingestionErrorCode` is non-empty (unknown codes use the generic default); raw
 * server/RAG text is only used when no code is present.
 */
export function resolveIngestionFailureText({
  ingestionError,
  ingestionErrorCode,
  fileUploadStatus,
  sourceContentType,
  sourceFileName,
  translate,
}: ResolveIngestionFailureTextInput): string {
  const sourceContext: IngestionFailureSourceContext = {
    sourceContentType,
    sourceFileName,
  }

  if (fileUploadStatus === 'FAILED') {
    return translate('status.uploadFailed', UPLOAD_FAILED_DEFAULT)
  }

  const trimmedCode =
    typeof ingestionErrorCode === 'string' ? ingestionErrorCode.trim() : ''
  if (trimmedCode.length > 0) {
    return getIngestionFailureMessage(trimmedCode, translate, sourceContext)
  }

  const trimmedError =
    typeof ingestionError === 'string' ? ingestionError.trim() : ''
  if (trimmedError.length > 0) {
    return trimmedError
  }

  return getIngestionFailureMessage(
    ingestionErrorCode,
    translate,
    sourceContext
  )
}

/** Mapped failure line for FAILED documents; never dumps raw pipeline errors. */
export function getMappedDocumentIngestionFailure(
  ingestionStatus: string | null | undefined,
  ingestionErrorCode: string | null | undefined,
  translate: IngestionFailureTranslate = (_key, defaultValue) => defaultValue,
  context?: IngestionFailureSourceContext
): string | null {
  if (ingestionStatus !== 'FAILED') {
    return null
  }
  const trimmedCode =
    typeof ingestionErrorCode === 'string' ? ingestionErrorCode.trim() : ''
  if (!trimmedCode) {
    return null
  }
  return getIngestionFailureMessage(trimmedCode, translate, context)
}

/** Shorten for select / list labels (e.g. manual validation document dropdown). */
export function truncateIngestionHint(text: string, maxLen = 56): string {
  if (text.length <= maxLen) {
    return text
  }
  return `${text.slice(0, Math.max(0, maxLen - 1))}…`
}
